/**
 * WebGL green-screen keyer for answer videos.
 *
 * Videos are rendered on a flat green background; "greenness" (g - max(r, b))
 * becomes transparency, and green spill on edges is clamped away.
 */

const VERT = `
attribute vec2 p;
varying vec2 uv;
void main() {
  uv = vec2(p.x * 0.5 + 0.5, 0.5 - p.y * 0.5);
  gl_Position = vec4(p, 0.0, 1.0);
}`;

const FRAG = `
precision mediump float;
uniform sampler2D tex;
varying vec2 uv;
const float LO = 0.10;  // greenness below this stays fully opaque
const float HI = 0.28;  // greenness above this is fully transparent
void main() {
  vec3 c = texture2D(tex, uv).rgb;
  float green = c.g - max(c.r, c.b);
  float a = 1.0 - smoothstep(LO, HI, green);
  c.g = min(c.g, max(c.r, c.b) + 0.02);  // despill
  gl_FragColor = vec4(c * a, a);          // premultiplied alpha
}`;

export class ChromaKeyer {
  readonly canvas = document.createElement("canvas");
  private readonly gl: WebGLRenderingContext;
  private readonly tex: WebGLTexture;

  static create(): ChromaKeyer | null {
    try {
      return new ChromaKeyer();
    } catch (err) {
      console.warn("[live-avatar] WebGL unavailable, videos will show their background", err);
      return null;
    }
  }

  private constructor() {
    const gl = this.canvas.getContext("webgl", { premultipliedAlpha: true, preserveDrawingBuffer: true });
    if (!gl) throw new Error("no webgl");
    this.gl = gl;

    const shader = (type: number, src: string) => {
      const s = gl.createShader(type)!;
      gl.shaderSource(s, src);
      gl.compileShader(s);
      if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(s) ?? "shader");
      return s;
    };
    const prog = gl.createProgram()!;
    gl.attachShader(prog, shader(gl.VERTEX_SHADER, VERT));
    gl.attachShader(prog, shader(gl.FRAGMENT_SHADER, FRAG));
    gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) throw new Error(gl.getProgramInfoLog(prog) ?? "link");
    gl.useProgram(prog);

    gl.bindBuffer(gl.ARRAY_BUFFER, gl.createBuffer());
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
    const loc = gl.getAttribLocation(prog, "p");
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);

    this.tex = gl.createTexture()!;
    gl.bindTexture(gl.TEXTURE_2D, this.tex);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  }

  /** Key the current video frame into this.canvas. */
  draw(video: HTMLVideoElement): boolean {
    if (video.readyState < 2 || !video.videoWidth) return false;
    const { gl, canvas } = this;
    if (canvas.width !== video.videoWidth || canvas.height !== video.videoHeight) {
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      gl.viewport(0, 0, canvas.width, canvas.height);
    }
    gl.bindTexture(gl.TEXTURE_2D, this.tex);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, video);
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT);
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    return true;
  }
}
